import {ClockCircleOutlined, CloseCircleFilled, LoadingOutlined, WarningOutlined} from '@ant-design/icons';
import {Button, notification, Popconfirm, Spin, Table, Tooltip} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import React, {useEffect, useState} from 'react';
import {useHistory} from "react-router-dom";
import styles from "./QueryProgress.module.scss";
import queryHistoryStyles from "./QueryHistory.module.scss";
import {apiFetchQueries, apiUpdateQuery} from '../../containers/QueryBuilder/queryBuilderApi';
import {UserQuery} from '../../containers/QueryBuilder/types/UserQuery';
import {getQueryBuilderCohortAnalyserRoute} from "../../pages/routes";
import {QueryBuilderTemplate} from './QueryBuilderTemplate';
import {queryDetailHtml} from "./QueryHistory";
import {QueryStatus} from '../../containers/QueryBuilder/types/Query';
import {queryBuilderApiUrl} from "../../actions/api";
import JsFileDownloader from "js-file-downloader";
import type {NotificationPlacement} from 'antd/es/notification';
import {QueriesDropDown} from "./QueriesDropDown";
import {generateQueryHistoryViewTitle} from "./queryHistoryUtil";
import NewQueryButton from "./NewQueryButton";
import {GenericSearch} from "../AppSearch/GenericSearch";
import {useAppSelector} from "../../hooks";
import {hasCompanyQueryHistoryViewPermission} from "../../actions/userSlice";

interface QueryProgressProps {
    isCohortAnalyser: boolean
}

export const QueryProgress = ({isCohortAnalyser}: QueryProgressProps) => {

    const FILTER = 'myquery'
    const [list, setList] = useState<UserQuery[] | undefined>(undefined)
    const history = useHistory()
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        apiFetchQueries(FILTER, isCohortAnalyser).then(i => {
            setList(i)
        })
    }, [isCohortAnalyser])

    const calculateDaysUntilExpiry = (dateString: number, daysValidUntil: number) => {
        const oneDayInMilliSeconds = 24 * 60 * 60 * 1000
        const today = new Date().getTime()
        const uploadDate = new Date(dateString).getTime()
        return daysValidUntil - Math.round(Math.abs((today - uploadDate) / oneDayInMilliSeconds))
    }

    const openDownloadNotification = (notificationType: string) => {
        const placement: NotificationPlacement = 'top'
        if (notificationType === 'error') {
            notification.error({
                message: `Error`,
                description: 'The file could not be downloaded. Please try exporting the query again.',
                placement,
            });
        } else {
            notification.info({
                message: `Info`,
                description: 'File download started',
                placement,
            });
        }
    };

    const downloadFileFromS3 = async (fileName: string): Promise<void> => {
        const url = queryBuilderApiUrl() + `/download/${fileName}`
        new JsFileDownloader({
            method: "GET",
            url: url,
            timeout: 0,
        }).then(function () {
            // called when download ended
            openDownloadNotification('info')
        }).catch(function (error) {
            // called when an error occurred
            console.log(error)
            openDownloadNotification('error')
        })
    }

    const columns = (): ColumnsType<UserQuery> => {
        return [
            {
                title: 'Query',
                dataIndex: 'query',
                render: (value, record, index) => {
                    const html = queryDetailHtml(value)
                    const onClick = () => {
                        history.push(`${getQueryBuilderCohortAnalyserRoute(isCohortAnalyser)}?queryId=${record.queryId}`)
                    }
                    return <div onClick={onClick} dangerouslySetInnerHTML={{__html: html}}
                                className={'clickable'}></div>
                },
                filteredValue: [searchTerm],
                onFilter: (input, record) => {
                    let searchInput = input.toString().toLowerCase()
                    let query = record.query.toString().toLowerCase()
                    let createdOn = record.createdOn.toString().toLowerCase()
                    let status = record.status.toString().toLowerCase()
                    return (status?.includes(searchInput) || query.includes(searchInput) || createdOn.includes(searchInput))
                },
            },
            {
                title: 'Duration',
                dataIndex: 'totalTimes',
                render: (value, record, index) => {
                    if (record.status === 'FINISHED') {
                        return `${value} secs`
                    }
                    return null
                },
                sorter: (a, b) => (a.totalTimes ?? '-1').localeCompare(b.totalTimes ?? '-1'),
            },
            {
                title: 'Date',
                dataIndex: 'createdOn',
                render: (value, record, index) => new Date(value).toLocaleDateString('en-GB'),
                sorter: (a, b) => a.createdOn - b.createdOn,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                render: (value) => value,
                sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? ''),
            },
            {
                title: 'Large Report Exports',
                dataIndex: 'largeReportExports',
                render: (value, record) => {

                    if (record.largeExport) {
                        let daysUntilExpiry = calculateDaysUntilExpiry(record.uploadedOn, 7);
                        if (record.uploadedToS3) {
                            if (daysUntilExpiry <= 0) {
                                return <>
                                    <div className={styles.exportExpired} data-testid='link-expired'>
                                        <WarningOutlined/> Download link expired.
                                    </div>
                                    <div className={styles.exportStatusInfo}>
                                        Please export query results again
                                    </div>
                                </>
                            } else {
                                return <div className={styles.exportStatus} data-testid='link-available'>
                                    <Button style={{borderRadius: '5px'}}
                                            data-testid={'large-export-download-button'}
                                            type='primary'
                                            onClick={() => {downloadFileFromS3(record.s3FileName)}}
                                    >
                                        Download
                                    </Button> <br/>
                                    <ClockCircleOutlined style={{color: 'green'}}/>
                                    <span className={styles.exportStatusInfo}> Expires in {daysUntilExpiry} days</span>
                                </div>
                            }
                        } else {

                            if (record.exportFileSizeOverLimit) {
                                return <div className={styles.exportFileSizeOverLimit} data-testid='s3-limit-reached'>
                                    <Tooltip placement='right'
                                             title='Please rerun your query with more filters or contact the Vantage Support team'
                                             color='#9EAB65'
                                    >
                                        <WarningOutlined/> <span>The resulting export file is too large to process</span>
                                    </Tooltip>
                                </div>
                            } else {
                                return <div className={styles.exportInProgress} data-testid='export-in-progress'>
                                    <Tooltip placement='right'
                                             title='You will receive an email when the export has completed.'
                                             color='#9EAB65'
                                    >
                                        <LoadingOutlined/> <span> Export in progress...</span>
                                    </Tooltip>
                                </div>
                            }
                        }

                    }

                },
                sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? ''),
            },
            {
                title: 'Cancel',
                dataIndex: 'delete',
                render: (value, record, index) => {
                    if (record.status === QueryStatus.NOT_RUNNING || record.status === QueryStatus.RUNNING) {
                        const onConfirm = () => {
                            apiUpdateQuery(record.queryId, {cancel: true})
                                .then(() => {
                                    apiFetchQueries(FILTER, isCohortAnalyser)
                                        .then(i => {
                                            setList(i)
                                        })
                                })
                        }
                        return <Popconfirm
                            title={'Do you want to cancel this query?'}
                            onConfirm={onConfirm}
                            okText="Yes"
                            cancelText="No"
                        >
                            <CloseCircleFilled style={{color: '#C44632'}}/>
                        </Popconfirm>
                    } else {
                        return null
                    }
                }
            },
        ]
    }

    const hasCompanyQueryHistoryAccess = useAppSelector(hasCompanyQueryHistoryViewPermission)

    const buttons = [
        ...(!isCohortAnalyser ? [
            <NewQueryButton key='1'
                            onClick={() => history.push(getQueryBuilderCohortAnalyserRoute(isCohortAnalyser))}
            />
        ] : []),

        <QueriesDropDown key='2'
                         isCohortAnalyser={isCohortAnalyser}
                         hasCompanyQueryHistoryAccess={hasCompanyQueryHistoryAccess}
        />
    ]

    return <>
        <QueryBuilderTemplate buttons={buttons} isCohortAnalyser={isCohortAnalyser}>

            <div className={queryHistoryStyles.queryProgressViewTitle}>
                {generateQueryHistoryViewTitle(history.location.pathname.split('/').pop())}
            </div>

            <GenericSearch placeholder="Search..."
                           className={queryHistoryStyles.queryProgressSearchBar}
                           allowClear
                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />

            {
                list ?
                <Table className={queryHistoryStyles.queryTable}
                       data-testid='query-progress-table'
                       rowKey='queryId'
                       columns={columns()}
                       dataSource={list}
                       pagination={{position: ['topRight', 'bottomRight']}}
                       bordered
                />
                : <Spin/>
            }
        </QueryBuilderTemplate>
    </>

}